import React, { FC, useEffect, useRef, useState, useCallback } from 'react'
import images from '../../../app/utlis/image'
import { trackClarityEvent } from '../../../app/utlis/ClarityAnalytics'
import { trackFirebaseEvent } from '../../../app/utlis/FirebaseAnalytics'
import { CLARITY_EVENTS } from '../../../app/utlis/EventTypes'
import homeBgVideo from '../../../app/assets/images/homepage/homebgvideo.mp4'
import homeimagee from '../../../app/assets/images/homepage/homeimagee.webp'
import homeimage2 from '../../../app/assets/images/homepage/homeimage2.webp'

interface BannerHomeComponentProps {
  homePageData: any
  onVideoReady?: () => void
}

const clamp = (v: number, min = 0, max = 1) => Math.min(Math.max(v, min), max)
const remap = (v: number, inMin: number, inMax: number) =>
  clamp((v - inMin) / (inMax - inMin))
const easeInOut = (t: number) =>
  t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t
const easeInOutCubic = (t: number) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2

const BannerHomeComponent: FC<BannerHomeComponentProps> = ({
  homePageData,
  onVideoReady,
}) => {
  const phoneNumber = '7016941771'
  const rafRef = useRef<number | null>(null)
  const currentRef = useRef(0)
  const targetRef = useRef(0)
  const [progress, setProgress] = useState(0)
  const sectionRef = useRef<HTMLElement>(null)

  useEffect(() => { onVideoReady?.() }, [onVideoReady])

  // ── RAF lerp loop ──────────────────────────────────────────────────────────
  const animate = useCallback(() => {
    const diff = targetRef.current - currentRef.current
    if (Math.abs(diff) > 0.00003) {
      currentRef.current += diff * 0.07   // slightly snappier lerp
      setProgress(currentRef.current)
    }
    rafRef.current = requestAnimationFrame(animate)
  }, [])

  useEffect(() => {
    const handleScroll = () => {
      // 2× window.innerHeight = ~2 full scrolls to complete everything
      const triggerEnd = window.innerHeight * 2
      targetRef.current = clamp(window.scrollY / triggerEnd)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    rafRef.current = requestAnimationFrame(animate)
    return () => {
      window.removeEventListener('scroll', handleScroll)
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [animate])

  const handleStartScopingConversation = () => {
    const payload = { source: 'banner_home' }
    trackClarityEvent(CLARITY_EVENTS.START_SCOPING_CONVERSATION_CLICKED, payload)
    trackFirebaseEvent(CLARITY_EVENTS.START_SCOPING_CONVERSATION_CLICKED, payload)
    window.location.href = '/book'
  }

  const handleSeeOurWork = () => {
    const payload = { source: 'banner_home' }
    trackClarityEvent(CLARITY_EVENTS.SEE_OUR_WORK_CLICKED, payload)
    trackFirebaseEvent(CLARITY_EVENTS.SEE_OUR_WORK_CLICKED, payload)
    window.location.href = '/portfolio'
  }

  const handleWhatsAppClick = () => {
    const payload = { source: 'banner_home' }
    trackFirebaseEvent(CLARITY_EVENTS.WHATSAPP_CLICKED, payload)
    trackClarityEvent(CLARITY_EVENTS.WHATSAPP_CLICKED, payload)
    window.open(`https://wa.me/${phoneNumber}`, '_blank')
  }

  const handleEmailClick = () => {
    const payload = { source: 'banner_home' }
    trackFirebaseEvent(CLARITY_EVENTS.EMAIL_CLICKED, payload)
    trackClarityEvent(CLARITY_EVENTS.EMAIL_CLICKED, payload)
    window.open(
      `https://mail.google.com/mail/?view=cm&fs=1&to=${homePageData?.company_email}`
    )
  }

  const handlePhoneClick = () => {
    const payload = { source: 'banner_home' }
    trackFirebaseEvent(CLARITY_EVENTS.PHONE_CLICKED, payload)
    trackClarityEvent(CLARITY_EVENTS.PHONE_CLICKED, payload)
    window.open(`tel:${homePageData?.company_number}`)
  }

  const p = progress

  // ── PHASE 1 (p: 0 → 0.55): image zooms from center to full-screen ─────────
  // Starts perfectly centered, scales up to cover viewport
  const zoomEase = easeInOutCubic(remap(p, 0, 0.55))
  const imageWidthVw = 75 + zoomEase * 27       // 75vw → 102vw (slight overshoot for full cover)
  const imageHeightVh = 65 + zoomEase * 37       // 65vh → 102vh
  const borderRadius = (1 - zoomEase) * 20      // 20px → 0px
  const extraScale = 1 + zoomEase * 0.50      // subtle scale boost

  // image fades out as zoom completes
  const imageOpacity = 1 - easeInOutCubic(remap(p, 0.42, 0.58))

  // video fades out as zoom starts
  const videoOpacity = 1 - easeInOut(remap(p, 0.05, 0.45))

  // ── PHASE 2 (p: 0.50 → 1.0): homeimage2 + content fades in ─────────────────
  const image2Opacity = easeInOut(remap(p, 0.48, 0.65))
  const contentOpacity = easeInOut(remap(p, 0.60, 0.82))
  const contentY = (1 - easeInOutCubic(remap(p, 0.65, 0.95))) * 45

  // ── SOCIAL: slides left as zoom begins, returns when content appears ──────
  const socialHideEase = easeInOut(remap(p, 0.0, 0.15))
  const socialShowEase = easeInOut(remap(p, 0.60, 0.78))
  const socialOpacity = socialHideEase < 1 ? 1 - socialHideEase : socialShowEase
  const socialX = socialHideEase < 1 ? -70 * socialHideEase : -70 + 70 * socialShowEase

  return (
    // 300vh = ~3 scroll lengths — enough to feel deliberate without dragging
    <div style={{ height: '300vh' }}>
      <section
        ref={sectionRef}
        className="sticky top-0 z-40 w-full overflow-hidden bg-[#070b16]"
        style={{ height: '100vh' }}
      >
        {/* ── VIDEO BG ── */}
        <video
          autoPlay muted loop playsInline preload="auto"
          className="absolute inset-0 h-full w-full object-cover"
          style={{ opacity: videoOpacity }}
        >
          <source src={homeBgVideo} type="video/mp4" />
        </video>

        <div
          className="
    absolute bottom-0 left-0 w-full
    h-[500px]
    md:h-[480px]
    lg:h-[400px]
    xl:h-[320px]
    xl:h-[380px]
  "
          style={{
            background:
              'linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0.85) 40%, rgba(0,0,0,0) 100%)',
            opacity: clamp(1 - zoomEase * 0.6),
            zIndex: 1,
          }}
        />

        {/* ── PHASE 2: homeimage2 full-screen bg ── */}
        <div
          className="absolute inset-0 z-20"
          style={{
            opacity: image2Opacity,
            backgroundImage: `url(${homeimage2})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div
          className="absolute inset-0 z-[21] bg-black/30"
          style={{ opacity: image2Opacity }}
        />

        {/* ── PHASE 2: content overlay ── */}
        <div
          className="absolute inset-0 z-[22] flex flex-col items-center justify-center gap-5 px-6 text-center"
          style={{
            opacity: contentOpacity,
            pointerEvents: contentOpacity < 0.1 ? 'none' : 'auto',
          }}
        >
          <h2
            className="font-google-sans-bold text-white select-none max-w-4xl text-[16px] md:text-[23px] lg:text-[30px] xl:text-[32px] "
            style={{
              letterSpacing: '0.02em',
              lineHeight: 1.15,
              transform: `translateY(${contentY}px)`,
            }}
          >
            AI Automation & Agentic Systems
            <br /> We Build the Ops Infrastructure Scaling Businesses Run On
          </h2>

          {/* Frosted pill */}
          <div
            style={{
              transform: `translateY(${contentY * 1.1}px)`,
              background: '#FFFFFF26',
              backdropFilter: 'blur(27px)',
              WebkitBackdropFilter: 'blur(27px)',
              borderRadius: 12,
              padding: 'clamp(10px, 1.2vw, 22px) clamp(18px, 2.5vw, 36px)',
            }}
          >
            <p
              className="text-white font-google-sans-medium"
              style={{
                fontSize: 'clamp(12px, 1.3vw, 15px)',
                letterSpacing: '0.03em',
                lineHeight: 2,
              }}
            >
              Custom AI Agents &nbsp;·&nbsp; Workflow Automation &nbsp;·&nbsp; SaaS Engineering
              <br />
              Compliance Automation &nbsp;·&nbsp;
              Fintech &amp; Web3 Development
            </p>
          </div>

          {/* Paragraph */}
          <p
            className="text-white max-w-3xl font-google-sans"
            style={{
              fontSize: 'clamp(13px, 1.1vw, 16px)',
              lineHeight: 1.85,
              transform: `translateY(${contentY * 1.3}px)`,
            }}
          >
            The Sattva Tech is a specialist AI and automation engineering firm serving founders
            and CTOs across the US, UK, EU, and Southeast Asia. We don&apos;t build demos — we ship
            production‑grade agentic systems, intelligent workflows, and scalable backend
            infrastructure that changes how your business actually operates.
          </p>

          {/* CTAs */}
          <div
            className="flex flex-wrap items-center justify-center gap-3 mt-1 font-google-sans"
            style={{ transform: `translateY(${contentY * 1.5}px)` }}
          >
            <button
              onClick={handleStartScopingConversation}
              style={{
                background: 'linear-gradient(118.8deg, #229CFF 27.32%, #3E62FD 91.18%)',
                border: 'none',
                borderRadius: 10,
                padding: 'clamp(10px, 1vw, 14px) clamp(22px, 2vw, 32px)',
                color: '#fff',
                fontSize: 'clamp(13px, 1.1vw, 16px)',
                fontWeight: 600,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                letterSpacing: '0.01em',
              }}
            >
              Start a scoping conversation →
            </button>
            <button
              onClick={handleSeeOurWork}
              style={{
                background: 'linear-gradient(118.8deg, #229CFF 27.32%, #3E62FD 91.18%)',
                border: 'none',
                borderRadius: 10,
                padding: 'clamp(10px, 1vw, 14px) clamp(22px, 2vw, 32px)',
                color: '#fff',
                fontSize: 'clamp(13px, 1.1vw, 16px)',
                fontWeight: 600,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                letterSpacing: '0.01em',
              }}
            >
              See our work ↗
            </button>
          </div>
        </div>

        {/* ── PHASE 1: homeimagee — always centered, zooms to fill screen ── */}
        {/* Mobile / Tablet Image */}
        <div
          className="absolute z-10 top-[60%] w-[75%] block md:hidden"
          style={{
            left: '50%',
            transform: `translate(-50%, -50%) scale(${extraScale})`,
            height: `${imageHeightVh}vh`,
            opacity: imageOpacity,
          }}
        >
          {/* Text Above Image */}
          <div
            className="absolute left-1/2 -translate-x-1/2 w-[90vw] text-center pointer-events-none"
            style={{
              bottom: '80%', // places text directly above image
              marginBottom: '24px', // gap between text and image
              zIndex: 20,
            }}
          >
            <h1
              className="font-google-sans-bold text-white text-[20px] md:text-[32px]"
              style={{
                lineHeight: 1.4,
                textShadow: '0 1px 20px rgba(0,0,0,0.8)',
              }}
            >
              <span className="block">AI Automation & </span>
              <span className="block">Agentic Systems</span>
              <span className="block">We Build the Ops Infrastructure</span>
              <span className="block">Scaling Businesses Run On</span>
            </h1>
          </div>

          {/* Image */}
          <div
            style={{
              width: '100%',
              height: '100%',
              borderRadius: `${borderRadius}px`,
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            <img
              src={homeimagee}
              alt=""
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                objectPosition: 'center',
                display: 'block',
              }}
            />

            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-2 px-4 md:hidden z-30"
              style={{ width: 'min(80%, 280px)' }}
            >
              {/* <button
        onClick={() => (window.location.href = '/book')}
        className="w-full rounded-full bg-[#229CFF] px-0 py-2 text-[10px] font-semibold text-white"
        style={{ boxShadow: '0 12px 30px rgba(34, 156, 255, 0.24)' }}
      >
        Start a scoping conversation →
      </button> */}
              <button
                onClick={handleSeeOurWork}
                style={{
                  background: 'linear-gradient(118.8deg, #229CFF 27.32%, #3E62FD 91.18%)',
                  border: 'none',
                  borderRadius: 10,
                  padding: 'clamp(10px, 1vw, 14px) clamp(22px, 2vw, 32px)',
                  color: '#fff',
                  fontSize: 'clamp(13px, 1.1vw, 16px)',
                  fontWeight: 600,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  letterSpacing: '0.01em',
                }}
              >
                See our work ↗
              </button>
            </div>
          </div>
        </div>

        {/* Desktop Image */}
        <div
          className="absolute z-10 top-[60%] hidden md:block"
          style={{
            left: '50%',
            transform: `translate(-50%, -50%) scale(${extraScale})`,
            width: `${imageWidthVw}vw`,
            height: `${imageHeightVh}vh`,
            opacity: imageOpacity,
            borderRadius: `${borderRadius}px`,
            overflow: 'hidden',
          }}
        >
          <img
            src={homeimagee}
            alt=""
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              objectPosition: 'center',
              display: 'block',
            }}
          />

          {/* Desktop Text Inside Image */}
          <div
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
            style={{ opacity: imageOpacity }}
          >
            <div
              className="text-center px-[8%]"
              style={{ transform: 'translateY(-18%)' }}
            >
              <h1
                className="font-google-sans-bold text-white text-[25px] xl:text-[30px] 2xl:text-[45px]"
                style={{
                  lineHeight: 1.5,
                  textShadow: '0 1px 20px rgba(0,0,0,0.8)',
                }}
              >
                <span className="block">AI Automation & Agentic Systems</span>
                <span className="block">We Build the Ops Infrastructure</span>
                <span className="block">Scaling Businesses Run On</span>
              </h1>
            </div>
          </div>
        </div>

        {/* ── SOCIAL ACTIONS ── */}
        <div
          className="fixed left-[2%] top-[30%] z-[55] flex flex-col gap-2 rounded-full bg-black/60 p-2 md:left-[3%] md:top-[40%] md:gap-3 md:p-3"
          style={{
            opacity: socialOpacity,
            transform: `translateX(${socialX}px)`,
            pointerEvents: socialOpacity < 0.1 ? 'none' : 'auto',
          }}
        >
          <button
            onClick={handleWhatsAppClick}
            className="flex items-center justify-center rounded-full bg-[#0B0B0B] p-3 md:p-5"
          >
            <img src={images.WPICON} alt="" className="h-5 w-5 md:h-7 md:w-7" />
          </button>
          <button
            onClick={handleEmailClick}
            className="flex items-center justify-center rounded-full bg-[#0B0B0B] p-3 md:p-5"
          >
            <img src={images.MAILICON} alt="" className="h-5 w-5 md:h-7 md:w-7" />
          </button>
          <button
            onClick={handlePhoneClick}
            className="flex items-center justify-center rounded-full bg-[#0B0B0B] p-3 md:p-5"
          >
            <img src={images.CUSTOMERICON} alt="" className="h-5 w-5 md:h-7 md:w-7" />
          </button>
        </div>

      </section>
    </div>
  )
}

export default BannerHomeComponent